import { useState } from 'react'
import { motion } from 'framer-motion'
import { Download, FileText, Calendar, Mail, MessageCircle, TrendingUp } from 'lucide-react'
import { db } from '../../firebase'
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore'
import * as XLSX from 'xlsx'

const ExportSection = () => {
  const [exporting, setExporting] = useState(false)

  const exportToExcel = (data, sheetName, filename) => {
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      alert('Nuk ka të dhëna për të eksportuar!')
      return
    }

    const headers = Object.keys(data[0])
    const csv = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || ''
          return `"${String(value).replace(/"/g, '""')}"`
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const handleExportChats = async () => {
    setExporting(true)
    try {
      const sessionsSnap = await getDocs(collection(db, 'chatSessions'))
      const data = sessionsSnap.docs.map(doc => ({
        ID: doc.id,
        Emri: doc.data().userName,
        Email: doc.data().userEmail,
        Data: doc.data().startedAt?.toDate().toLocaleString('sq-AL'),
        Status: doc.data().status
      }))
      exportToCSV(data, 'chat_sessions')
    } catch (error) {
      console.error('Error exporting chats:', error)
      alert('Gabim gjatë eksportimit!')
    } finally {
      setExporting(false)
    }
  }

  const handleExportContacts = async () => {
    setExporting(true)
    try {
      const contactsSnap = await getDocs(collection(db, 'contactSubmissions'))
      const data = contactsSnap.docs.map(doc => ({
        Emri: doc.data().name,
        Email: doc.data().email,
        Telefon: doc.data().phone || '',
        Mesazhi: doc.data().message,
        Data: doc.data().createdAt?.toDate().toLocaleString('sq-AL'),
        'I Lexuar': doc.data().read ? 'Po' : 'Jo'
      }))
      exportToCSV(data, 'contacts')
    } catch (error) {
      console.error('Error exporting contacts:', error)
      alert('Gabim gjatë eksportimit!')
    } finally {
      setExporting(false)
    }
  }

  const handleExportApplications = async () => {
    setExporting(true)
    try {
      const appsSnap = await getDocs(collection(db, 'projectApplications'))
      const data = appsSnap.docs.map(doc => ({
        Emri: doc.data().name,
        Email: doc.data().email,
        Telefon: doc.data().phone,
        Kompania: doc.data().company || '',
        'Tipi Projektit': doc.data().projectType,
        Buxheti: doc.data().budget,
        Timeline: doc.data().timeline,
        Përshkrimi: doc.data().description,
        Data: doc.data().createdAt?.toDate().toLocaleString('sq-AL')
      }))
      exportToCSV(data, 'applications')
    } catch (error) {
      console.error('Error exporting applications:', error)
      alert('Gabim gjatë eksportimit!')
    } finally {
      setExporting(false)
    }
  }

  const handleExportConsultations = async () => {
    setExporting(true)
    try {
      const consSnap = await getDocs(collection(db, 'consultationRequests'))
      const data = consSnap.docs.map(doc => ({
        Emri: doc.data().name,
        Email: doc.data().email,
        Telefon: doc.data().phone,
        'Data e Preferuar': doc.data().preferredDate,
        'Ora e Preferuar': doc.data().preferredTime,
        'Tipi Konsultimit': doc.data().consultationType,
        Mesazhi: doc.data().message || '',
        'Data e Aplikimit': doc.data().createdAt?.toDate().toLocaleString('sq-AL')
      }))
      exportToCSV(data, 'consultations')
    } catch (error) {
      console.error('Error exporting consultations:', error)
      alert('Gabim gjatë eksportimit!')
    } finally {
      setExporting(false)
    }
  }

  const handleExportMonthlyReport = async () => {
    setExporting(true)
    try {
      const now = new Date()
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const firstDayTimestamp = Timestamp.fromDate(firstDayOfMonth)

      // Get all data from current month
      const [chatsSnap, contactsSnap, appsSnap, consSnap, reviewsSnap, messagesSnap] = await Promise.all([
        getDocs(query(collection(db, 'chatSessions'), where('startedAt', '>=', firstDayTimestamp))),
        getDocs(query(collection(db, 'contactSubmissions'), where('createdAt', '>=', firstDayTimestamp))),
        getDocs(query(collection(db, 'projectApplications'), where('createdAt', '>=', firstDayTimestamp))),
        getDocs(query(collection(db, 'consultationRequests'), where('createdAt', '>=', firstDayTimestamp))),
        getDocs(collection(db, 'reviews')),
        getDocs(collection(db, 'chatMessages'))
      ])

      // Prepare workbook with multiple sheets
      const wb = XLSX.utils.book_new()

      // Summary Sheet
      const summary = [[
        { v: 'RAPORT MUJOR - ' + now.toLocaleDateString('sq-AL', { month: 'long', year: 'numeric' }).toUpperCase(), t: 's', s: { font: { bold: true, sz: 16 } } }
      ], [], [
        'Metrika', 'Vlera'
      ], [
        'Total Chat Sessions', chatsSnap.size
      ], [
        'Total Mesazhe', messagesSnap.size
      ], [
        'Kontakte të Reja', contactsSnap.size
      ], [
        'Aplikacione', appsSnap.size
      ], [
        'Konsultime', consSnap.size
      ], [
        'Recensione', reviewsSnap.size
      ], [], [
        'Data e Gjenerimit:', new Date().toLocaleString('sq-AL')
      ]]

      const wsSummary = XLSX.utils.aoa_to_sheet(summary)
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Përmbledhje')

      // Chat Sessions Sheet
      const chatsData = chatsSnap.docs.map(doc => ({
        'ID': doc.id,
        'Emri': doc.data().userName,
        'Email': doc.data().userEmail,
        'Data Fillimit': doc.data().startedAt?.toDate().toLocaleString('sq-AL'),
        'Status': doc.data().status,
        'Assigned To': doc.data().assignedToStaffName || 'Bot'
      }))
      const wsChats = XLSX.utils.json_to_sheet(chatsData)
      XLSX.utils.book_append_sheet(wb, wsChats, 'Chat Sessions')

      // Contacts Sheet
      const contactsData = contactsSnap.docs.map(doc => ({
        'Emri': doc.data().name,
        'Email': doc.data().email,
        'Telefon': doc.data().phone || '',
        'Mesazhi': doc.data().message,
        'Data': doc.data().createdAt?.toDate().toLocaleString('sq-AL')
      }))
      const wsContacts = XLSX.utils.json_to_sheet(contactsData)
      XLSX.utils.book_append_sheet(wb, wsContacts, 'Kontakte')

      // Applications Sheet
      const appsData = appsSnap.docs.map(doc => ({
        'Emri': doc.data().name,
        'Email': doc.data().email,
        'Kompania': doc.data().company || '',
        'Tipi Projektit': doc.data().projectType,
        'Buxheti': doc.data().budget,
        'Data': doc.data().createdAt?.toDate().toLocaleString('sq-AL')
      }))
      const wsApps = XLSX.utils.json_to_sheet(appsData)
      XLSX.utils.book_append_sheet(wb, wsApps, 'Aplikacione')

      // Consultations Sheet
      const consData = consSnap.docs.map(doc => ({
        'Emri': doc.data().name,
        'Email': doc.data().email,
        'Telefon': doc.data().phone,
        'Data e Preferuar': doc.data().preferredDate,
        'Tipi': doc.data().consultationType,
        'Data': doc.data().createdAt?.toDate().toLocaleString('sq-AL')
      }))
      const wsCons = XLSX.utils.json_to_sheet(consData)
      XLSX.utils.book_append_sheet(wb, wsCons, 'Konsultime')

      // Write file
      const monthName = now.toLocaleDateString('sq-AL', { month: 'long', year: 'numeric' })
      XLSX.writeFile(wb, `Raport_Mujor_${monthName.replace(' ', '_')}.xlsx`)
      
      alert('✅ Raporti mujor u shkarkua me sukses!')
    } catch (error) {
      console.error('Error exporting monthly report:', error)
      alert('❌ Gabim gjatë eksportimit të raportit!')
    } finally {
      setExporting(false)
    }
  }

  const exportOptions = [
    {
      title: 'Raport Mujor Komplet',
      description: 'Eksporto të gjitha të dhënat e muajit në Excel',
      icon: TrendingUp,
      color: 'from-gold-500 to-bronze-600',
      action: handleExportMonthlyReport,
      featured: true
    },
    {
      title: 'Chat Sessions',
      description: 'Eksporto të gjitha sesionet e chat-it',
      icon: MessageCircle,
      color: 'from-green-500 to-emerald-600',
      action: handleExportChats
    },
    {
      title: 'Forma e Kontaktit',
      description: 'Eksporto mesazhet nga forma e kontaktit',
      icon: Mail,
      color: 'from-blue-500 to-cyan-600',
      action: handleExportContacts
    },
    {
      title: 'Aplikacionet',
      description: 'Eksporto aplikacionet për projekte',
      icon: FileText,
      color: 'from-orange-500 to-red-600',
      action: handleExportApplications
    },
    {
      title: 'Konsultimet',
      description: 'Eksporto kërkesat për konsultime',
      icon: Calendar,
      color: 'from-purple-500 to-pink-600',
      action: handleExportConsultations
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-display font-bold text-gradient-gold mb-2">
          Eksporto të Dhënat
        </h2>
        <p className="text-beige-300">Shkarko të dhënat në format CSV për analizë</p>
      </div>

      <div className="space-y-6">
        {/* Featured Monthly Report */}
        {exportOptions.filter(opt => opt.featured).map((option, index) => {
          const Icon = option.icon
          return (
            <motion.div
              key={option.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-gold-500/20 to-bronze-500/20 backdrop-blur-sm border-2 border-gold-500/50 rounded-2xl p-8 hover:border-gold-400 transition-all relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-400/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-16 h-16 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center shadow-gold-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <span className="px-3 py-1 bg-gold-400/20 text-gold-400 text-xs font-bold rounded-full border border-gold-400/30">
                    ⭐ REKOMANDUAR
                  </span>
                </div>
                <h3 className="text-2xl font-display font-bold text-gold-400 mb-2">
                  {option.title}
                </h3>
                <p className="text-beige-200 mb-6">
                  {option.description}
                </p>
                <button
                  onClick={option.action}
                  disabled={exporting}
                  className="btn-primary w-full md:w-auto justify-center disabled:opacity-50"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {exporting ? 'Duke eksportuar...' : 'Shkarko Raportin Excel'}
                </button>
              </div>
            </motion.div>
          )
        })}

        {/* Other Export Options */}
        <div className="grid md:grid-cols-2 gap-6">
          {exportOptions.filter(opt => !opt.featured).map((option, index) => {
          const Icon = option.icon
          return (
            <motion.div
              key={option.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6 hover:border-gold-500/40 transition-all"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-display font-bold text-gold-400 mb-2">
                {option.title}
              </h3>
              <p className="text-beige-300 text-sm mb-4">
                {option.description}
              </p>
              <button
                onClick={option.action}
                disabled={exporting}
                className="btn-primary w-full justify-center disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Duke eksportuar...' : 'Eksporto CSV'}
              </button>
            </motion.div>
          )
          })}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-blue-400 mb-2">💡 Informacion</h3>
        <ul className="space-y-2 text-sm text-beige-200">
          <li>• Të dhënat eksportohen në format CSV që mund të hapen në Excel</li>
          <li>• Të gjitha të dhënat përfshihen, edhe ato të fshira nga interface</li>
          <li>• Emri i file-it përfshin datën e eksportimit</li>
          <li>• CSV përdor encoding UTF-8 për karaktere shqipe</li>
        </ul>
      </div>
    </div>
  )
}

export default ExportSection
